import { Observable, Subscription } from "rxjs";
import { Inject } from "@angular/core";
import { Component, HostListener, OnInit, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DOCUMENT } from "@angular/common";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import localeEs from "@angular/common/locales/es";
import localeEn from "@angular/common/locales/en";
import { AuthenticationService } from "../services/authentication.service";
import { CommonService } from "../services/common.service";
import { CacheService } from "../services/cache.service";
import { DIMBO_CONSTANT } from "../utils/constant";
import { VinSearchService } from "../services/vin-search.service";
import { MaintenancePlanResponse } from "../model/maintenance-plan-response";
import { ScreenSizeService } from "../services/screen-size.service";
import { SubmissionHistory } from "../model/submission-history";
import { AppConfig } from "../app.config";
import { OAuthService } from "angular-oauth2-oidc";
import { truncate } from "fs";
import jwtDecode from "jwt-decode";
import { RefreshTokenService } from "../services/refresh-token.service";
import { RootService } from "../services/root.service";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { AccessTokenService } from "../services/access-token.service";

registerLocaleData(localeFr, "fr");
registerLocaleData(localeEs, "es");
registerLocaleData(localeEn, "en");

/** RootComponent: Orchestrates authentication (token ingest/refresh), session/user checks, locale + translation setup, initial VIN search, tab labeling/navigation, and high-level error/state handling for the application shell. */
@Component({
  selector: "app-root",
  templateUrl: "./root.component.html",
  styleUrls: ["./root.component.scss"],
})
export class RootComponent implements OnInit {
  title = "dmbr-scf";

  brandCareTitle: string = DIMBO_CONSTANT.BRAND_CARE_TITLE;

  selectedTab = 0;
  selected = new FormControl(0);

  isTokenValidated: boolean = false;
  isErrorDetected: boolean = false;
  errorMessage = "";
  errorType: string = "";

  reloadFirstTab: boolean = false;
  isVehicleInfoUpdated: boolean;
  isAdditionalInfoUpdated: boolean;
  isMaintenanceTabSelected: boolean;
  isShTabSelected: boolean;
  shscrolledcount: number = 0;
  isCentralUser: boolean = false;
  submissionMode: boolean = true; // CAP-39922 DMBR-64 New Submission History Change
  showNewSubmissionHistory: boolean = false; // CAP-39922 DMBR-64 New Submission History Change
  showVehicleInfo: boolean = false; // CAP-39922 DMBR-64 driven by VIN presence
  defaultVin: string = ""; // CAP-39922 DMBR-64 VIN pre-fill for submissionMode

  userId?: string | null = null;
  username?: string | null = null;
  accessToken?: string | null = null;
  incomingUserId?: string | null = null;
  storedUserId?: string | null = null;
  tokenRefreshInterval: any = null;

  allowedReferrers: string[] = [];

  maintenancePlan: MaintenancePlanResponse | null = null;

  tabs: string[] = [];
  shTab: string = ""; // Default value
  mTab: string = ""; // Default value

  // Variable's realted to vin search
  vinSearchResults: any[] = [];
  vinSearchForm: FormGroup;
  searchingVin: boolean = false;
  selectedVehicleInfo: any = null;
  vinSearchErrorMessage: string = "";

  // Variable used to show error page if anything goes wrong while submitting the form
  isErrorOccured: boolean = false;
  statusType: "error" | "success" | "warning" = "error";
  statusMessage = "";
  showStatusMessage = false;

  isMobile: boolean = false;

  private tokenValidationErrorSub: Subscription;

  constructor(
    private cacheService: CacheService,
    private commonService: CommonService,
    private toastrService: ToastrService,
    private translationService: TranslateService,
    private authenticationService: AuthenticationService,
    private fb: FormBuilder,
    private vinSearchService: VinSearchService,
    @Inject(DOCUMENT) private document: Document,
    private spinner: NgxSpinnerService,
    private ngZone: NgZone,
    private screenSizeService: ScreenSizeService,
    private appConfig: AppConfig,
    private refreshTokenService: RefreshTokenService,
    private oauthService: OAuthService,
    private rootService: RootService,
    private titleService: Title,
    private router: Router,
    private accessTokenService: AccessTokenService,
  ) {
    this.translationService.setDefaultLang("en");
    this.vinSearchForm = this.fb.group({
      vin: [
        "",
        [
          Validators.required,
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{17}$/),
        ],
      ],
    });

    this.tokenValidationErrorSub =
      this.authenticationService.tokenValidationError.subscribe(
        (msg: string) => {
          this.isErrorDetected = true;
          this.errorMessage = msg;
          this.spinner.hide();
        },
      );
  }

  ngOnDestroy() {
    if (this.tokenValidationErrorSub) {
      this.tokenValidationErrorSub.unsubscribe();
    }
  }

  /**
   * Initializes the component and establishes an authenticated session.
   * Flow:
   * 1. Optional referrer check (MPSA); aborts on invalid/missing referrer.
   * 2. Show spinner; extract token + incoming user.
   * 3. Load existing access token; decode stored user.
   * 4. Short‑circuit checks: user change, session expiry, valid session, refresh attempt.
   * 5. Fallback: start interactive login if none succeeded.
   * Side effects: updates state, storage, invokes auth services.
   */
  async ngOnInit() {
    // const mpsaFlag = this.appConfig.mpsaFlag;

    // if (mpsaFlag === 'Y') {

    //   this.allowedReferrers = this.appConfig.allowedReferrers || [];

    //   const referrer = document.referrer;

    //   const isAllowed = referrer && this.allowedReferrers.some(domain => referrer.includes(domain));

    //   if (!referrer || !isAllowed) {
    //     this.isErrorDetected = true;
    //     this.errorMessage = 'Direct access is not allowed. Please access the application from service box only.';
    //     return;
    //   }
    // }

    this.spinner.show();
    const tokenFromUrl = this.handleTokenFromUrl();
    const incomingUserId = this.getIncomingUserId(tokenFromUrl);

    const accessTokenFromStorage = localStorage.getItem("access_token");
    this.accessToken = accessTokenFromStorage ?? null;


    if (this.accessToken) {
      this.accessTokenService.decodeAndStoreToken(this.accessToken);
      const payload = this.accessTokenService.getDecodedToken();
      this.storedUserId =
        payload?.userId ?? payload?.sub ?? payload?.username ?? null;
    
    } else {
      this.storedUserId = null;
    }


    if (this.isUserChanged(this.storedUserId, incomingUserId)) return;

    if (this.isSessionExpired()) return;
    if (
      this.isValidSession(this.accessToken, this.storedUserId, incomingUserId)
    )
      return;
 
    if (await this.tryRefreshToken(incomingUserId)) return;


    this.startNormalLogin();
  }

  /**
   * Handles the retrieval and processing of a token from the URL.
   *
   * This method checks if a token is present in the URL. If not, it attempts to retrieve
   * the token from the cache. If the token is still not found, it displays an error message.
   * Once a valid token is obtained, it is stored in the cache, and the token parameter
   * is removed from the URL to clean up the browser's address bar.
   *
   * @private
   */
  private handleTokenFromUrl(): string {
    let tokenFromUrl = this.getTokenFromUrl();

    if (!tokenFromUrl) {
      tokenFromUrl = this.cacheService.getItem(
        DIMBO_CONSTANT.CACHE_SERVICE_BOX_TOKEN,
      );
    }

    if (!tokenFromUrl) {
      this.showTokenMissingError();
      return "";
    }

    this.cacheService.setItem(
      DIMBO_CONSTANT.CACHE_SERVICE_BOX_TOKEN,
      tokenFromUrl,
    );

    // Clean URL
    const url = new URL(window.location.href);
    if (url.searchParams.has("token")) {
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    }

    return tokenFromUrl;
  }

  /**
   * Retrieves the incoming user ID from the provided JWT token (or cache fallback).
   * @param token - The JWT token received from URL or other source.
   * @returns The extracted user ID (sub, userID, or username) or empty string if unavailable/invalid.
   */
  private getIncomingUserId(token: string): string {
    const incomingToken = token;
    if (!incomingToken) return "";
    try {
      const decoded: any = jwtDecode(incomingToken);
      return decoded.sub || decoded.userID || decoded.username || "";
    } catch (err) {
      return "";
    }
  }

  /**
   * Determines if the user has changed by comparing the stored user ID with the incoming user ID.
   * If the user has changed, it clears all data and logs out the user to the identity provider.
   *
   * @param storedUserId - The user ID currently stored in the application, or `null` if not set.
   * @param incomingUserId - The user ID being passed into the application.
   * @returns `true` if the user has changed (i.e., the stored user ID and incoming user ID are different),
   *          otherwise `false`.
   */
  private isUserChanged(
    storedUserId: string | null,
    incomingUserId: string,
  ): boolean {
    if (storedUserId && incomingUserId && storedUserId !== incomingUserId) {
      this.rootService.clearAll();
      this.authenticationService.logoutToIdp();
      return true;
    }
    return false;
  }

  /**
   * Checks if the user's session has expired based on the last login date and time.
   *
   * This method retrieves the last login timestamp from the `rootService` and calculates
   * the difference between the current time and the last login time. If the difference
   * exceeds 24 hours, it clears all session data, logs the user out via the IDP, and
   * returns `true` indicating that the session has expired.
   *
   * @returns {boolean} `true` if the session has expired, otherwise `false`.
   */
  private isSessionExpired(): boolean {
    const lastLogin = this.rootService.getLastLoginDateTime();
    if (lastLogin) {
      const lastLoginDate = new Date(lastLogin);
      const now = new Date();
      const diffHours =
        (now.getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60);
      if (diffHours > 24) {
        this.rootService.clearAll();
        this.authenticationService.logoutToIdp();
        return true;
      }
    }
    return false;
  }

  /**
   * Validates the current user session based on the provided access token and user IDs.
   *
   * @param accessToken - The access token to validate. Can be a string or null.
   * @param storedUserId - The user ID stored in the session. Can be a string or null.
   * @param incomingUserId - The user ID provided for the current session validation.
   * @returns A boolean indicating whether the session is valid.
   */
  private isValidSession(
    accessToken: string | null,
    storedUserId: string | null,
    incomingUserId: string,
  ): boolean {
    
    if (
      accessToken &&
      this.isTokenValid(accessToken) &&
      storedUserId === incomingUserId
    ) {
   
      this.afterSuccessfulLogin();
      return true;
    }

    return false;
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * Validates the refreshed token's user ID against the incoming user ID.
   *
   * @param incomingUserId - User ID to validate against the refreshed token.
   * @returns `true` if refresh is successful, otherwise `false`.
   *
   * @remarks
   * - Clears session and logs out if user ID mismatches or refresh fails.
   * - Decodes the refreshed token to extract the user ID.
   */
  // NOTED: Check if we can avoid async operation here
  private async tryRefreshToken(incomingUserId: string): Promise<boolean> {
    const refreshToken = this.rootService.getRefreshToken();



    if (refreshToken) {
      try {
    
        const tokenResponse = await this.requestAccessTokenWithRefreshToken(
          refreshToken,
          this.appConfig.clientID,
        );

        if (tokenResponse.access_token) {
          this.rootService.setAccessToken(tokenResponse.access_token);

          if (tokenResponse.refresh_token) {
            this.rootService.setRefreshToken(tokenResponse.refresh_token);
          
          }

          this.accessTokenService.decodeAndStoreToken(
            tokenResponse.access_token,
          );
          const payload = this.accessTokenService.getDecodedToken();
          this.storedUserId =
            payload?.userId ?? payload?.sub ?? payload?.username ?? null;

          if (this.storedUserId === incomingUserId) {
            this.afterSuccessfulLogin();
            return true;
          } else {

            this.rootService.clearAll();
            this.authenticationService.logoutToIdp();
            return true;
          }
        } else {
          this.rootService.clearAll();
          this.authenticationService.logoutToIdp();
          return true;
        }
      } catch (err) {
        this.rootService.clearAll();
        this.authenticationService.logoutToIdp();
        return true;
      }
    }
    return false;
  }

  /** Starts the normal login process via IDP. */
  private startNormalLogin() {
    this.startIdpLogin();
  }

  /**
   * Validates the provided JWT token by checking its expiration time.
   *
   * @param token - The JWT token to validate.
   * @returns `true` if the token is valid (i.e., it has an expiration time and the current time is before the expiration time),
   *          otherwise `false`.
   * @throws This method does not throw errors directly but logs a warning if token decoding fails.
   */
  isTokenValid(token: string): boolean {
    try {
      const cached: any = this.accessTokenService.getDecodedToken();
      const now = Math.floor(Date.now() / 1000);

      if (cached && typeof cached.exp === "number") {
        return cached.exp > now;
      }

      // Fallback: decode if not cached or missing exp
      const fresh: any = jwtDecode(token);

      return typeof fresh.exp === "number" && fresh.exp > now;
    } catch (err) {
      return false;
    }
  }

  /**
   * Configures OAuthService and initiates the IDP login process.
   * Handles user changes, login flow, and errors like "access_denied".
   */
  async startIdpLogin() {
    const config = this.appConfig.getAppConfig();

    this.oauthService.configure({
      issuer: config.issuerURL,
      clientId: config.clientID,
      redirectUri: config.redirectURI,
      scope: config.scopeVar,
      responseType: "code",
      requireHttps:
        config.requireHttps !== undefined ? config.requireHttps : true,
      showDebugInformation: true,
    });

    this.oauthService.setStorage(localStorage);

    const accessToken = this.oauthService.getAccessToken();

    if (accessToken) {
      this.accessTokenService.decodeAndStoreToken(accessToken);
      const payload = this.accessTokenService.getDecodedToken();
      this.storedUserId =
        payload?.userId ?? payload?.sub ?? payload?.username ?? null;
    }

    const incomingToken = this.cacheService.getItem(
      DIMBO_CONSTANT.CACHE_SERVICE_BOX_TOKEN,
    );

    const incomingUserId = this.getIncomingUserId(incomingToken);

    const userChanged =
      this.storedUserId &&
      incomingUserId &&
      this.storedUserId !== incomingUserId;

    if (userChanged) {
      this.oauthService.initLoginFlow(null, { prompt: "login" });
      return;
    }

    this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((loggedIn) => {
        if (loggedIn) {
          const now = new Date().toISOString();
          this.rootService.setLastLoginDateTime(now);
          this.afterSuccessfulLogin();
        } else {
          this.oauthService.initLoginFlow();
        }
      })
      .catch((err) => {
        if (err?.params?.error === "access_denied") {
          const userLang =
            this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_LANGUAGE_RAW) ||
            "en";
          this.translationService.use(userLang);
          this.translationService
            .get("dimbo.i18n.error.access.denied")
            .subscribe((translatedMsg) => {
              this.toastrService.error(translatedMsg, "", {
                timeOut: 0,
                extendedTimeOut: 0,
                closeButton: true,
              });
            });
          // const logoutUrl = `${config.logoutURL}`;
          // setTimeout(() => {
          //   window.location.href = logoutUrl;
          // }, 5000);
          // return;

          const logoutUrl = `${config.logoutURL}`;
          setTimeout(() => {
            window.location.href = logoutUrl;
            setTimeout(() => {
              window.close();
              setTimeout(() => {
                if (!window.closed) {
                  alert(
                    "Please close this tab manually and come again from service box.",
                  );
                }
              }, 200);
            }, 2000); // Try to close after redirect
          }, 5000);

          // const logoutUrl = `${config.logoutURL}`;
          // console.log('[IDP] Logging out in background:', logoutUrl);

          // Create hidden iframe for logout
          // const iframe = document.createElement('iframe');
          // iframe.style.display = 'none';
          // iframe.src = logoutUrl;
          // document.body.appendChild(iframe);

          // setTimeout(() => {
          //   window.close();
          //   setTimeout(() => {
          //     alert("Please close this tab manually and come again from service box.");
          //   }, 200);
          // }, 2000);
        }

        // if (err?.params?.error === 'access_denied') {
        //   const targetResource = `${config.redirectURI.replace(/\/$/, '')}/idp-error`;
        //   const logoutUrl = `${config.logoutURL}?TargetResource=${encodeURIComponent(targetResource)}`;
        //   console.log('[IDP] Access denied. Redirecting to logout URL with target resource:', logoutUrl);
        //   window.location.href = logoutUrl;
        //   return;
        // }
      });
  }

  /**
   * Requests a new access token using the provided refresh token and client ID.
   *
   * @param refreshToken - The refresh token used to obtain a new access token.
   * @param clientId - The client ID associated with the request.
   * @returns A promise that resolves with the response containing the new access token.
   */
  async requestAccessTokenWithRefreshToken(
    refreshToken: string,
    clientId: string,
  ): Promise<any> {
    return this.refreshTokenService.requestAccessTokenWithRefreshToken(
      refreshToken,
      clientId,
    );
  }

  /**
   * Executes post-login logic: validates token, sets user details, and initializes the app.
   * Logs out the user on token issues or user ID mismatches.
   */
  afterSuccessfulLogin() {
    const token = this.cacheService.getItem(
      DIMBO_CONSTANT.CACHE_SERVICE_BOX_TOKEN,
    );

    if (!token) {
      this.showTokenMissingError();
      return;
    } else if (
      token === "null" ||
      token === "" ||
      !/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)
    ) {
      this.isErrorDetected = true;
      this.translationService
        .get("dimbo.i18n.error.token.invalid")
        .subscribe((translatedMsg) => {
          this.errorMessage = translatedMsg;
        });
      localStorage.clear();
      this.spinner.hide();
      return;
    }

    // --- NEW LOGIC: Compare user IDs (with debug logs) ---

    let incomingUserId = "";
    let accessTokenUserId = "";
    let incomingLang = "en";
    try {
      const decodedIncoming: any = jwtDecode(token);
      incomingUserId =
        decodedIncoming.sub ||
        decodedIncoming.userID ||
        decodedIncoming.username ||
        "";
      incomingLang = decodedIncoming.language || "en";

      // CAP-39922 DMBR-64 New Submission History Change
      const submissionModeRaw = decodedIncoming.submissionMode;
      // const submissionMode =
      //   typeof submissionModeRaw === "boolean"
      //     ? submissionModeRaw
      //     : String(submissionModeRaw).toLowerCase() === "true";

      // FIX (backward-compatible):
      const submissionMode =
        submissionModeRaw == null
          ? true // absent field = legacy behavior (form + old history)
          : typeof submissionModeRaw === "boolean"
            ? submissionModeRaw
            : String(submissionModeRaw).toLowerCase() === "true";

      this.cacheService.setItem(
        DIMBO_CONSTANT.CACHE_SUBMISSION_MODE,
        submissionMode,
      );
      // CAP-39922 DMBR-64 New Submission History Change End
    } catch (err) {}

    const accessTokenAfterLogin = this.oauthService.getAccessToken();
    this.accessTokenService.decodeAndStoreToken(accessTokenAfterLogin);
    const decodedAccess: any = this.accessTokenService.getDecodedToken();
    accessTokenUserId =
      decodedAccess?.userId ||
      decodedAccess?.sub ||
      decodedAccess?.username ||
      "";

    // Compare user IDs
    if (
      incomingUserId &&
      accessTokenUserId &&
      incomingUserId !== accessTokenUserId
    ) {
      this.translationService.use(incomingLang);
      this.translationService
        .get("dimbo.i18n.error.userid.mismatch")
        .subscribe((translatedMsg) => {
          this.toastrService.warning(translatedMsg, "", {
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: true,
          });
        });
      localStorage.clear();
      setTimeout(() => {
        this.authenticationService.logoutToIdp();
      }, 2000);
      return;
    }

    // --- END NEW LOGIC ---

    this.authenticationService
      .validateToken(token)
      .then(() => {
        this.isCentralUser = this.cacheService.getItem(
          DIMBO_CONSTANT.CACHE_IS_CENTRAL_USER,
        );
        this.setBrandCareTitleFromCache();

        const userLang =
          this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_LANGUAGE_RAW) ||
          "en";
        this.translationService.use(userLang);

        this.translationService
          .get(["dimbo.i18n.Allucare.tab", "dimbo.i18n.History.tab"])
          .subscribe(
            (translations) => {
              // CAP-39922 DMBR-64 New Submission History Change
              this.mTab = translations["dimbo.i18n.Allucare.tab"];
              this.shTab = translations["dimbo.i18n.History.tab"];
              this.resolveSubmissionMode();
              this.tabs = this.showNewSubmissionHistory
                ? [this.shTab]
                : [this.mTab, this.shTab];
              // CAP-39922 DMBR-64 New Submission History Change End
              this.searchVinByDefault();
              const supportedLocales = [
                "en",
                "fr",
                "es",
                "de",
                "it",
                "nl",
                "pt",
                "pl",
              ];
              const userLocale =
                this.cacheService.getItem(
                  DIMBO_CONSTANT.CACHE_JWT_LANGUAGE_RAW,
                ) || "en";
              const localeToLoad = supportedLocales.includes(userLocale)
                ? userLocale
                : "en";
              this.localeInitializer(localeToLoad)
                .then(() => {
                  this.isTokenValidated = true;
                  this.spinner.hide();
                })
                .catch(() => {
                  this.spinner.hide();
                });
            },
            () => {
              this.handleTranslationFallback(this.isCentralUser);
            },
          );
      })
      .catch((error) => {
        this.isErrorDetected = true;
        if (error && error.status === 0) {
          this.translationService
            .get("dimbo.i18n.error.backend.unreachable")
            .subscribe((translatedMsg) => {
              this.errorMessage = translatedMsg;
            });
        }
        localStorage.clear();
        this.spinner.hide();
      });
  }

  /**
   * Displays an error message indicating that a token is missing.
   *
   * This method sets the `isErrorDetected` flag to `true`, retrieves a localized
   * error message using the translation service, and assigns it to the `errorMessage` property.
   * It also clears the local storage and hides the spinner.
   *
   * Note: The Toastr service call for displaying a warning message is commented out.
   */
  showTokenMissingError() {
    this.isErrorDetected = true;
    this.translationService
      .get("dimbo.i18n.error.token.missing")
      .subscribe((translatedMsg) => {
        // this.toastrService.warning(translatedMsg, '', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
        this.errorMessage = translatedMsg;
      });
    localStorage.clear();
    this.spinner.hide();
  }

  /**
   * Updates status type, message, and visibility based on the event.
   * @param event - Contains `type` and `message` of the status.
   */
  onStatusMessage(event: { type: string; message: string }) {
    this.statusType = event.type as any;
    this.statusMessage = event.message;
    this.showStatusMessage = true;
  }

  /** Called when the new Submission History component starts a VIN search. */
  onSubmissionHistoryVinSearch(vin: string): void {
    // Only set loading state; do NOT call handleVinSearch to avoid a duplicate API call.
    // Results will arrive via vinSearchCompleted.
    this.searchingVin = true;
    this.vinSearchErrorMessage = "";
  }

  /**
   * Called when the new Submission History component's VIN API call completes.
   * Handles vehicle-info display logic without making another API call.
   */
  onVinSearchCompleted(event: {
    vin: string;
    results: SubmissionHistory[];
    hasError: boolean;
  }): void {
    this.searchingVin = false;
    const isPayloadVinSearch =
      event.vin === this.defaultVin && !!this.defaultVin;

    if (event.hasError) {
      this.vinSearchResults = [];
      if (isPayloadVinSearch) {
        // Auto-search with payload VIN returned an error — keep cache, no toast.
        this.selectedVehicleInfo = { shouldUpdateVehicleInfo: false };
      } else {
        // Manual VIN search failed — show N/A fallback only if vehicle info is visible.
        this.selectedVehicleInfo = {
          isFromSearch: true,
          shouldUpdateVehicleInfo: true,
          showFallback: true,
        };
      }
      return;
    }

    this.vinSearchResults = event.results || [];
    this.vinSearchErrorMessage = "";

    if (this.vinSearchResults.length > 0) {
      this.selectedVehicleInfo = {
        ...this.vinSearchResults[0],
        isFromSearch: true,
        shouldUpdateVehicleInfo: true,
        showFallback: false,
      };
      // Do NOT change showVehicleInfo here — it stays as set by resolveSubmissionMode().
      // Vehicle info is only visible when a valid default VIN was in the payload.
    } else {
      if (isPayloadVinSearch) {
        // Auto-search: VIN from token/cache — keep cached vehicle info.
        this.selectedVehicleInfo = { shouldUpdateVehicleInfo: false };
      } else {
        // Manual search with no results — show N/A fallback (only visible if showVehicleInfo is already true).
        this.selectedVehicleInfo = {
          isFromSearch: true,
          shouldUpdateVehicleInfo: true,
          showFallback: true,
        };
      }
    }
  }

  /** Updates `selectedVehicleInfo` if `shouldUpdateVehicleInfo` is true. */
  handleVehicleInfoChange(vehicleInfo: any) {
    if (vehicleInfo?.shouldUpdateVehicleInfo) {
      this.selectedVehicleInfo = vehicleInfo;
    }
  }

  setBrandCareTitleFromCache() {
    const brandCareTitle = this.cacheService.getItem(
      DIMBO_CONSTANT.CACHE_BRANDCARE_TITLE,
    );
    this.brandCareTitle = brandCareTitle || DIMBO_CONSTANT.BRAND_CARE_TITLE;
    this.titleService.setTitle(this.brandCareTitle);
  }

  // CAP-39922 DMBR-64 New Submission History Change
  private resolveSubmissionMode(): void {
    const cached = this.cacheService.getItem(
      DIMBO_CONSTANT.CACHE_SUBMISSION_MODE,
    );

    if (typeof cached === "boolean") {
      this.submissionMode = cached;
    } else {
      this.submissionMode = String(cached).toLowerCase() !== "false";
    }
    // Central user always see new Submission History
    // Authorized Repairer only see new Submission History when submissionMode is false
    this.showNewSubmissionHistory = this.isCentralUser || !this.submissionMode;

    this.defaultVin =
      this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN) || "";

    const isDefaultVinValid = /^[A-Z0-9]{17}$/i.test(this.defaultVin);
    this.showVehicleInfo = this.submissionMode || isDefaultVinValid;

    // CAP: 39922 DMBR-64 New Submission History Change End
  }

  /**
   * Searches for a VIN (Vehicle Identification Number) using a default value stored in the cache.
   * If a default VIN is found, it triggers a search and handles the response or any errors that occur.
   *
   * - On success: Updates the VIN search results with the retrieved data.
   * - On error: Displays an appropriate error message based on the HTTP status code or a generic message for unexpected errors.
   *
   * The method also manages the `searchingVin` state to indicate whether a search is in progress.
   *
   * @private
   */
  private searchVinByDefault(): void {
    const defaultVin = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN);
    this.vinSearchErrorMessage = "";
    if (defaultVin) {
      this.searchingVin = true;
      this.vinSearchService.searchByVin(defaultVin).subscribe({
        next: (data: SubmissionHistory[]) => {
          this.vinSearchResults = data || [];
          this.searchingVin = false;
        },
        error: (err) => {
          if ([404, 400, 401, 500].includes(err.status)) {
            this.translationService
              .get(err.error.message || "dimbo.i18n.error.generic")
              .subscribe((translatedMsg) => {
                this.vinSearchErrorMessage = translatedMsg;
              });
          } else {
            this.translationService
              .get("dimbo.i18n.error.unexpected")
              .subscribe((translatedMsg) => {
                this.vinSearchErrorMessage = translatedMsg;
              });
          }

          this.vinSearchResults = [];
          this.searchingVin = false;
        },
      });
    }
  }

  /**
   * Switches to the history tab: clears error and sets selectedTab.
   */
  goToHistory() {
    this.isErrorDetected = false;
    this.selectedTab = this.tabs.indexOf(this.shTab);
  }

  closeTab() {
    window.close();
    setTimeout(() => {
      this.translationService
        .get("dimbo.i18n.error.tab.close")
        .subscribe((translatedMsg) => {
          alert(translatedMsg);
        });
    }, 200);
  }

  /**
   * VIN search: sets loading, clears error, calls service.
   * Success: assigns results (or empty), sets selectedVehicleInfo (first result or fallback).
   * Error: clears results, sets fallback, translates and shows toast (known status vs unexpected).
   * @param vin VIN string.
   */
  handleVinSearch(vin: string) {
    this.searchingVin = true;
    this.vinSearchErrorMessage = "";
    this.vinSearchService.searchByVin(vin).subscribe({
      next: (data: SubmissionHistory[]) => {
        this.vinSearchResults = data || [];
        this.searchingVin = false;
        this.vinSearchErrorMessage = "";
        if (this.vinSearchResults.length > 0) {
          this.selectedVehicleInfo = {
            ...this.vinSearchResults[0],
            isFromSearch: true,
            shouldUpdateVehicleInfo: true,
            showFallback: false, // D
          };
          // Always reveal vehicle-info when a VIN is found
          if (this.showNewSubmissionHistory) {
            this.showVehicleInfo = true;
          }
        } else {
          // this.selectedVehicleInfo = {
          //   isFromSearch: true,
          //   shouldUpdateVehicleInfo: true,
          //   showFallback: true,
          // };
          // If default VIN was present (showVehicleInfo already true) → keep shown as N/A
          // If default VIN was absent (showVehicleInfo false) → keep hidden
          // i.e. do NOT change showVehicleInfo here

          // No history found
          const isPayloadVinSearch = vin === this.defaultVin;

          if (isPayloadVinSearch) {
            // Auto-search: VIN came from token payload/cache.
            // Vehicle info is already loaded from cache in VehicleInfoComponent.ngOnInit().
            // Do not overwrite with fallback.
            this.selectedVehicleInfo = { shouldUpdateVehicleInfo: false };
          } else {
            // Manual search: user searched a different VIN — show N/A fallback.
            this.selectedVehicleInfo = {
              isFromSearch: true,
              shouldUpdateVehicleInfo: true,
              showFallback: true,
            };
          }
        }
      },
      error: (err) => {
        this.vinSearchResults = [];
        this.searchingVin = false;

        const isPayloadVinError = vin === this.defaultVin;
        if (isPayloadVinError) {
          // Auto-search with payload VIN returned an error (e.g. 404 = no history).
          // Keep vehicle info from cache — do NOT overwrite with fallback.
          this.selectedVehicleInfo = { shouldUpdateVehicleInfo: false };
          // No error toast for auto-search: just silently show empty results.
        } else {
          // Manual VIN search failed — show N/A fallback and error toast.
          this.selectedVehicleInfo = {
            isFromSearch: true,
            shouldUpdateVehicleInfo: true,
            showFallback: true,
          };
          if ([404, 400, 401, 500].includes(err.status)) {
            this.translationService
              .get(err.error?.message || "dimbo.i18n.error.generic")
              .subscribe((translatedMsg) => {
                this.vinSearchErrorMessage = translatedMsg;
                this.toastrService.error(this.vinSearchErrorMessage);
              });
          } else {
            this.translationService
              .get("dimbo.i18n.error.unexpected")
              .subscribe((translatedMsg) => {
                this.vinSearchErrorMessage = translatedMsg;
                this.toastrService.error(this.vinSearchErrorMessage);
              });
          }
        }
      },
    });
  }

  /** Returns the "token" query param from the current URL, or '' if missing. */
  getTokenFromUrl(): string {
    const params = new URL(window.location.href).searchParams;
    return params.get("token") || "";
  }

  /**
   * Dynamically loads and registers locale data.
   * Falls back to 'en' if unsupported.
   * Supported: en, fr, es, de, it, nl, pt, pl.
   * @param localeId locale code.
   * @returns Promise after registration.
   */
  async localeInitializer(localeId: string): Promise<any> {
    const supportedLocales = ["en", "fr", "es", "de", "it", "nl", "pt", "pl"];
    if (!supportedLocales.includes(localeId)) {
      localeId = "en";
    }
    const module = await import(
      /* webpackInclude: /\.js$/ */
      `@angular/common/locales/${localeId}.js`
    );
    return registerLocaleData(module.default);
  }

  /**
   * Fallback: force English translations and tab labels when localization fails.
   * Central users: only History; others: History + Allucare.
   * Initializes 'en' locale; always hides spinner afterward.
   */
  handleTranslationFallback(isCentralUser: boolean) {
    this.translationService.use("en");
    this.translationService
      .get(["dimbo.i18n.Allucare.tab", "dimbo.i18n.History.tab"])
      .subscribe((translations) => {
        // New Submission History Change
        this.mTab = translations["dimbo.i18n.Allucare.tab"];
        this.shTab = translations["dimbo.i18n.History.tab"];
        this.resolveSubmissionMode();
        this.tabs = this.showNewSubmissionHistory
          ? [this.shTab]
          : [this.mTab, this.shTab];

        this.localeInitializer("en")
          .then(() => {
            this.isTokenValidated = true;
            this.spinner.hide();
          })
          .catch(() => {
            this.spinner.hide();
          });
      });
  }

  selectTab(event: any) {
    this.selectedTab = event.index;
  }

  onSwitchToFirst($event: any) {
    this.reloadFirstTab = true;
    this.selected.setValue(0);
  }

  reloadServiceHistory($event: any) {
    this.reloadFirstTab = false;
  }

  onVeichleUpdate(event: any) {
    this.isVehicleInfoUpdated = true;
  }

  onMaintenanceReload(event: any) {
    this.isVehicleInfoUpdated = false;
  }

  onAdditionInfoUpdated(event: any) {
    this.isAdditionalInfoUpdated = true;
  }

  onTabChange(event: any) {
    const index = event.index;
    this.isShTabSelected = index === 0;
    this.isMaintenanceTabSelected = index === 1;
    this.cacheService.setItem(
      DIMBO_CONSTANT.CACHE_CREATE_SERVICE_SCOPE,
      index === 0 ? false : true,
    );
  }

  shScrolled(event: any) {
    this.shscrolledcount = event.count;
    this.cacheService.setItem(
      DIMBO_CONSTANT.CACHE_SH_SCROLL_COUNT,
      this.shscrolledcount,
    );
  }
}
